import React, { useEffect, useRef, useState } from 'react';
import { FiGlobe, FiMenu, FiX } from 'react-icons/fi';

declare global {
  interface Window {
    loadGoogleTranslate?: (callback?: () => void) => void;
  }
}

function getActiveGoogleLang(): string {
  try {
    if (typeof document !== 'undefined') {
      const match = document.cookie.match(/googtrans=\/[a-z-]+\/([a-z-]+)/);
      if (match && match[1] && match[1] !== 'en') return match[1];
    }
  } catch {}
  try {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('siteLang') || 'en';
    }
  } catch {}
  return 'en';
}

export default function Header() {
  const [lang, setLang] = useState<string>(() => getActiveGoogleLang());
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const langs = [
    { code: 'en', label: 'EN', display: 'English' },
    { code: 'sv', label: 'SV', display: 'Svenska' },
    { code: 'es', label: 'ES', display: 'Español' },
    { code: 'de', label: 'DE', display: 'Deutsch' },
  ];

  const changeLanguage = (code: string) => {
    setLang(code);
    setOpen(false);
    localStorage.setItem('siteLang', code);
    const domain = window.location.hostname;
    if (code === 'en') {
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
      localStorage.removeItem('siteLang');
    } else {
      document.cookie = `googtrans=/en/${code}; path=/`;
      document.cookie = `googtrans=/en/${code}; path=/; domain=${domain}`;
    }
    const applyLanguage = () => {
      const combo = document.querySelector('.goog-te-combo') as HTMLSelectElement | null;
      if (combo) {
        combo.value = code;
        combo.dispatchEvent(new Event('change'));
      }
    };
    window.loadGoogleTranslate?.(() => window.setTimeout(applyLanguage, 0));
  };

  const navigateTo = (e: React.MouseEvent<HTMLAnchorElement>, to: string) => {
    e.preventDefault();
    if (window.location.pathname === to) return;
    window.history.pushState({}, '', to);
    window.dispatchEvent(new Event('app:navigate'));
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 px-4 pt-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl rounded-[28px] border border-[var(--line-soft)] bg-white/78 shadow-[0_24px_60px_-42px_rgba(8,31,53,0.65)] backdrop-blur-xl">
        <div className="flex items-center justify-between px-5 py-4 sm:px-7">
          <a href="/" onClick={(e) => navigateTo(e, '/')} className="text-xl font-extrabold tracking-tight sm:text-2xl">
            <span className="text-[var(--brand-ink)]">learnsocialai</span>
            <span className="text-[var(--brand-accent)]">care.com</span>
          </a>

          <nav className="hidden items-center gap-3 rounded-full border border-[var(--line-soft)] bg-[var(--surface-0)]/80 p-2 md:flex">
            <a href="/" onClick={(e) => navigateTo(e, '/')} className="rounded-full px-4 py-2 text-sm font-semibold text-[var(--text-muted)] transition-colors hover:bg-white hover:text-[var(--brand-ink)]">
              HOME
            </a>
            <a href="/faqs/" onClick={(e) => navigateTo(e, '/faqs/')} className="rounded-full px-4 py-2 text-sm font-semibold text-[var(--text-muted)] transition-colors hover:bg-white hover:text-[var(--brand-ink)]">
              FAQS
            </a>

            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setOpen(v => !v)}
                className="flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-semibold text-[var(--brand-ink)] transition-colors hover:text-[var(--brand-accent)]"
                aria-haspopup="true"
                aria-expanded={open}
                translate="no"
              >
                <FiGlobe className="h-4 w-4 text-[var(--brand-accent)]" aria-hidden="true" focusable="false" />
                <span>{langs.find(l => l.code === lang)?.label ?? 'EN'}</span>
              </button>

              {open && (
                <div className="absolute right-0 z-50 mt-3 w-44 rounded-2xl border border-[var(--line-soft)] bg-white py-1 shadow-[var(--shadow-soft)]">
                  {langs.map(l => (
                    <button
                      key={l.code}
                      onClick={() => changeLanguage(l.code)}
                      className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                        lang === l.code
                          ? 'bg-[var(--surface-3)] font-semibold text-[var(--brand-accent)]'
                          : 'text-[var(--text-muted)] hover:bg-[var(--surface-0)]'
                      }`}
                      translate="no"
                    >
                      {l.display}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <button
            onClick={() => setMobileOpen(v => !v)}
            className="rounded-full border border-[var(--line-soft)] bg-[var(--surface-0)] p-2 text-[var(--text-muted)] transition-colors hover:text-[var(--brand-accent)] md:hidden"
            aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <FiX className="h-5 w-5" aria-hidden="true" focusable="false" /> : <FiMenu className="h-5 w-5" aria-hidden="true" focusable="false" />}
          </button>
        </div>

        {mobileOpen && (
          <nav className="space-y-2 border-t border-[var(--line-soft)] px-5 pb-5 pt-3 md:hidden">
            <a href="/" onClick={(e) => navigateTo(e, '/')} className="block rounded-2xl bg-[var(--surface-0)] px-4 py-3 text-sm font-semibold text-[var(--brand-ink)]">
              HOME
            </a>
            <a href="/faqs/" onClick={(e) => navigateTo(e, '/faqs/')} className="block rounded-2xl bg-[var(--surface-0)] px-4 py-3 text-sm font-semibold text-[var(--brand-ink)]">
              FAQS
            </a>
            <div className="flex flex-wrap gap-2 pt-2">
              {langs.map(l => (
                <button
                  key={l.code}
                  onClick={() => {
                    changeLanguage(l.code);
                    setMobileOpen(false);
                  }}
                  className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                    lang === l.code
                      ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)] text-white'
                      : 'border-[var(--line-soft)] bg-white text-[var(--brand-ink)] hover:border-[var(--brand-accent)]'
                  }`}
                  translate="no"
                >
                  {l.display}
                </button>
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
